import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { flexRender, type RowData } from '@tanstack/react-table';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

import type { TableContentProps } from '../types';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { DraggableRow } from './DraggableRow';
import { EmptyTable } from './EmptyTable';
import { TableDndContext } from './TableDndContext';

export function TableContent<TData extends RowData>(
  props: TableContentProps<TData>,
) {
  const {
    table,
    columns,
    pending = false,
    loadingMessage,
    onRowClick,
    getRowClassName,
    renderSubRow,
    dnd,
  } = props;

  const { t } = useTranslation();
  const hasExpandableRows = !!renderSubRow;
  const isDndEnabled = !!dnd;
  const leadingCols = (hasExpandableRows ? 1 : 0) + (isDndEnabled ? 1 : 0);

  const rows = table.getRowModel().rows;
  const rowIds = rows.map((row) => row.id);

  const renderedRows = rows.map((row) => {
    const cells = row
      .getVisibleCells()
      .map((cell) => (
        <TableCell key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ));

    if (isDndEnabled) {
      return (
        <DraggableRow
          key={row.id}
          id={row.id}
          className={getRowClassName?.(row.original, row.index) || ''}
        >
          {cells}
        </DraggableRow>
      );
    }

    return (
      <Fragment key={row.id}>
        <TableRow
          data-state={row.getIsSelected() && 'selected'}
          className={`${getRowClassName?.(row.original, row.index) || ''} ${onRowClick && !hasExpandableRows ? 'cursor-pointer' : ''}`}
          onClick={() => !hasExpandableRows && onRowClick?.(row.original)}
        >
          {hasExpandableRows && (
            <TableCell>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => row.toggleExpanded()}
                className="size-8 p-0"
              >
                {row.getIsExpanded() ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
              </Button>
            </TableCell>
          )}
          {cells}
        </TableRow>
        {hasExpandableRows && row.getIsExpanded() && (
          <TableRow>
            <TableCell
              colSpan={columns.length + 1}
              className="p-0"
            >
              <div className="bg-muted/50 p-4">
                {renderSubRow(row.original)}
              </div>
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  });

  const tableElement = (
    <div
      className={
        isDndEnabled ? 'overflow-x-auto overflow-y-hidden' : 'overflow-x-auto'
      }
    >
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {isDndEnabled && <TableHead className="w-12" />}
              {hasExpandableRows && <TableHead className="w-12" />}
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="whitespace-normal"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {pending ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + leadingCols}
                className="h-24 text-center"
              >
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="text-primary size-4 animate-spin" />
                  <span>{loadingMessage || t('Loading...')}</span>
                </div>
              </TableCell>
            </TableRow>
          ) : rows.length ? (
            renderedRows
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length + leadingCols}
                className="h-24 text-center"
              >
                <EmptyTable />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  if (isDndEnabled && dnd) {
    return (
      <TableDndContext
        rowIds={rowIds}
        onReorder={dnd.onReorder}
      >
        {tableElement}
      </TableDndContext>
    );
  }

  return tableElement;
}
